/**
 * src/lib/auth.jsx — session state for the whole SPA.
 *
 * The token itself is an httpOnly cookie the JavaScript cannot see, so "am I
 * logged in?" can only be answered by asking the API. This context does that
 * once on boot via GET /api/auth/me and shares the answer, rather than every
 * protected page discovering it independently.
 *
 * `loading` matters: until /me has answered we know nothing, and redirecting
 * to /login during that window would bounce a signed-in user out on every
 * refresh.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api, setUnauthorisedHandler } from './api.js';
import { THEMES, setTheme } from './theme.js';

const AuthContext = createContext(null);

/**
 * Provides session state to the app.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function AuthProvider({ children }) {
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);

  // Only known themes apply, and only when the account actually has one set —
  // an artist who has never touched the picker keeps whatever is in
  // localStorage (or the default) rather than being reset to it on every load.
  const applyArtist = useCallback((next) => {
    setArtist(next);
    if (THEMES.includes(next?.theme)) setTheme(next.theme);
  }, []);

  useEffect(() => {
    // Any 401 from anywhere clears the session, which flips the protected
    // routes over to a redirect. One handler rather than per-call handling.
    setUnauthorisedHandler(() => setArtist(null));

    let cancelled = false;
    api
      .get('/api/auth/me')
      .then((res) => {
        if (!cancelled) applyArtist(res.artist);
      })
      .catch(() => {
        // 401 on boot simply means "not signed in" — not an error to surface.
        if (!cancelled) setArtist(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    applyArtist(res.artist);
    return res.artist;
  }, [applyArtist]);

  const register = useCallback(async (payload) => {
    const res = await api.post('/api/auth/register', payload);
    applyArtist(res.artist);
    return res.artist;
  }, [applyArtist]);

  const logout = useCallback(async () => {
    // Clear local state even if the network call fails — the user asked to be
    // signed out, and the cookie will expire regardless.
    try {
      await api.post('/api/auth/logout');
    } finally {
      setArtist(null);
    }
  }, []);

  const value = useMemo(
    () => ({ artist, loading, login, register, logout, isOwner: artist?.role === 'owner' }),
    [artist, loading, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Reads the session.
 *
 * @returns {{ artist: object|null, loading: boolean, isOwner: boolean,
 *   login: Function, register: Function, logout: Function }}
 * @throws {Error} If used outside AuthProvider — a wiring mistake worth
 *   failing loudly on.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
